import { Paperclip, Eye, Phone, MessageCircle, Mail, Copy, Send } from "lucide-react";
import { toast } from "@/lib/toast";
import { whatsappHref, whatsappCallHref, openZohoMail } from "@/lib/contactLinks";
import { useFilePreview } from "@/context/FilePreviewContext";
import { useTypologyIcon } from "@/components/crm/sales/SaleRow";

/** iter463 — carte rail (bande 30 px pleine couleur, icône blanche 13 px, titre 9 px 700 .1em) commune F2 / K1. */
export function RailCard({ color, titleColor = "#E8E8EC", icon: Icon, title, children, action, testId }) {
  return (
    <section className="flex" style={{ background: "#17171B", border: "1px solid #26262C", borderRadius: 10, overflow: "hidden", breakInside: "avoid", marginBottom: 8 }} data-testid={testId}>
      <div className="flex items-start justify-center shrink-0" style={{ width: 30, background: color, paddingTop: 9 }}><Icon size={13} color="#fff" /></div>
      <div className="flex flex-col min-w-0" style={{ flex: 1, padding: "8px 11px 10px", gap: 6 }}>
        <div className="flex items-center" style={{ gap: 8 }}><span style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: titleColor }}>{title}</span><span style={{ flex: 1 }} />{action}</div>
        {children}
      </div>
    </section>
  );
}

export const Dash = () => <span style={{ color: "#4A4A52" }}>—</span>;

/** 19.5 — ligne de champ : libellé 10 px #8A8A93 (118 px), valeur 11 px #E8E8EA, vide = « — ». */
export function FieldRow({ label, value, children, testId }) {
  const v = children ?? value;
  return (
    <div className="flex items-start min-w-0" style={{ gap: 8 }} data-testid={testId}>
      <span style={{ fontSize: 10, color: "#8A8A93", width: 118, flexShrink: 0, paddingTop: 1 }}>{label}</span>
      <span className="min-w-0" style={{ flex: 1, fontSize: 11, color: "#E8E8EA", wordBreak: "break-word" }}>{v === null || v === undefined || v === "" ? <Dash /> : v}</span>
    </div>
  );
}

const STAGE_PILL = { vente: ["CLIENT", "#4ADE80", "#14532D"], reservation: ["RÉSERVATION", "#FCD34D", "#713F12"], devis: ["DEVIS", "#FDBA74", "#7C2D12"], prospect: ["PROSPECT", "#A1A1AA", "#3F3F46"], perdu: ["PERDU", "#F87171", "#7F1D1D"] };
export function StagePill({ stage, testId }) {
  const [label, color, border] = STAGE_PILL[stage] || STAGE_PILL.prospect;
  return <span className="inline-flex items-center" style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".06em", padding: "2px 8px", borderRadius: 999, color, border: `1px solid ${border}`, lineHeight: "14px" }} data-testid={testId}>{label}</span>;
}

/** 18.3 — affichage groupé « +213 556 00 02 24 » ; le numéro enregistré ne change pas. */
export const fmtPhone = (raw) => {
  const d = String(raw || "").replace(/[^\d+]/g, "");
  if (!d) return raw || "";
  let n = d.startsWith("+") ? d.slice(1) : d.startsWith("00") ? d.slice(2) : d.startsWith("0") ? `213${d.slice(1)}` : d;
  if (!n.startsWith("213") || n.length < 12) return raw;
  const r = n.slice(3);
  return `+213 ${r.slice(0, 3)} ${r.slice(3, 5)} ${r.slice(5, 7)} ${r.slice(7)}`.trim();
};

const Act = ({ href, onClick, color, Icon, title, testId }) => {
  const st = { width: 26, height: 26, borderRadius: 7, border: "1px solid #33333B", color };
  return href ? <a href={href} title={title} className="inline-flex items-center justify-center shrink-0" style={st} data-testid={testId}><Icon size={12} /></a>
    : <button type="button" onClick={onClick} title={title} className="inline-flex items-center justify-center shrink-0" style={st} data-testid={testId}><Icon size={12} /></button>;
};

export function PhoneLine({ p, i, prefix }) {
  return (
    <div className="flex items-center min-w-0" style={{ gap: 6 }} data-testid={`${prefix}-phone-${i}`}>
      <Phone size={11} color="#4ADE80" />
      <span className="tabular-nums" style={{ fontFamily: "'JetBrains Mono', Consolas, monospace", fontSize: 12, fontWeight: 700, color: "#F0F0F4", flex: 1, whiteSpace: "nowrap" }} title={p.value}>{fmtPhone(p.value)}</span>
      <span style={{ fontSize: 10, color: "#8A8A93" }}>{p.label}</span>
      <Act href={whatsappCallHref(p.value)} color="#4ADE80" Icon={Phone} title="Appeler" testId={`${prefix}-call-${i}`} />
      <Act href={whatsappHref(p.value)} color="#4ADE80" Icon={MessageCircle} title="WhatsApp" testId={`${prefix}-wa-${i}`} />
    </div>
  );
}

export function EmailLine({ m, i, prefix }) {
  const copy = async () => { try { await navigator.clipboard.writeText(m.value); toast.success("Copié"); } catch { toast.error("Copie impossible"); } };
  return (
    <div className="flex items-center min-w-0" style={{ gap: 6 }} data-testid={`${prefix}-email-${i}`}>
      <Mail size={11} color="#93B4F5" />
      <span className="truncate" title={m.value} style={{ fontSize: 11.5, color: "#F0F0F4", flex: 1 }}>{m.value}</span>
      <span style={{ fontSize: 10, color: "#8A8A93" }}>{m.label}</span>
      <Act onClick={copy} color="#93C5FD" Icon={Copy} title="Copier" testId={`${prefix}-copy-${i}`} />
      <Act onClick={() => openZohoMail(m.value)} color="#93C5FD" Icon={Send} title="Envoyer un e-mail" testId={`${prefix}-send-${i}`} />
    </div>
  );
}

const fileSrc = (f) => f.url || f.path || `/crm/files/${f.file_id || f.id}/download`;
const fileName = (f) => f.name || f.filename || f.original_filename || "fichier";

/** 18.4 — fichier : trombone 13 px #4ADE80 + nom tronqué (complet au survol) + œil 24 px (contour #33333B, icône #93C5FD) → visionneuse. */
export function DocLine({ f, label, all = [], testId }) {
  const { openAuthenticatedUrl } = useFilePreview();
  const open = () => { const list = (all.length ? all : [f]).map((x) => ({ url: fileSrc(x), filename: fileName(x), contentType: x.content_type || "" })); const index = Math.max(0, all.indexOf(f)); return openAuthenticatedUrl(fileSrc(f), { filename: fileName(f), contentType: f.content_type || "", gallery: list, index }); };
  return (
    <div className="flex items-center min-w-0" style={{ gap: 6 }} data-testid={testId}>
      <Paperclip size={13} color="#4ADE80" className="shrink-0" />
      <span className="truncate" title={fileName(f)} style={{ fontSize: 11, color: "#E8E8EA", flex: 1 }}>{fileName(f)}</span>
      {label && <span className="truncate" style={{ fontSize: 10, color: "#8A8A93", maxWidth: 120 }}>{label}</span>}
      <button type="button" onClick={open} title="Voir" className="inline-flex items-center justify-center shrink-0" style={{ width: 24, height: 24, borderRadius: 6, border: "1px solid #33333B", color: "#93C5FD", background: "transparent" }} data-testid={`${testId}-eye`}><Eye size={13} /></button>
    </div>
  );
}

export const SALE_STATUS = { incomplete: ["À compléter", "#FCD34D"], active: ["Confirmé", "#4ADE80"], cancelled: ["Annulé", "#F87171"], deleted: ["Supprimé", "#8A8A93"], expired: ["Expiré", "#F87171"], converted: ["Converti", "#93C5FD"] };
export function StatusPill({ status, testId }) {
  const [l, c] = SALE_STATUS[status] || [status || "—", "#A1A1AA"];
  return <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".05em", padding: "1px 7px", borderRadius: 999, color: c, border: `1px solid ${c}55`, textTransform: "uppercase" }} data-testid={testId}>{l}</span>;
}

/** 18.2 — lots : icône typologie 13 px + n° monospace 700 dans la couleur de la typologie + code projet. */
export function LotChips({ lots = [], projectRef, testId }) {
  const typo = useTypologyIcon();
  return (
    <span className="inline-flex items-center flex-wrap" style={{ gap: 8 }} data-testid={testId}>
      {lots.map((l, i) => { const [Icon, color] = typo(l.type); return <span key={l.id || i} className="inline-flex items-center" style={{ gap: 4 }} title={l.type || ""}><Icon size={13} style={{ color }} /><span style={{ fontFamily: "'JetBrains Mono', Consolas, monospace", fontSize: 11, fontWeight: 700, color }}>{l.numero || "—"}</span></span>; })}
      {projectRef && <span style={{ fontSize: 10, color: "#8A8A93", fontFamily: "'JetBrains Mono', Consolas, monospace" }}>{projectRef}</span>}
    </span>
  );
}
